import { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2, Circle, Loader2, Trash2, Rocket, GraduationCap, Wallet, Flag,
} from 'lucide-react';
import { API, fmtDate, CATEGORY_META, PRIORITY_CLASS } from '../../lib/planConstants';

const Section = ({ title, icon: Icon, children, testid }) => (
  <div className="glass-card p-5" data-testid={testid}>
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-4 h-4 text-cyan-300" />
      <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">{title}</h3>
    </div>
    {children}
  </div>
);

const List = ({ items, empty = 'Not specified' }) =>
  (items || []).length ? (
    <ul className="space-y-1.5">
      {items.map((it, i) => (
        <li key={i} className="text-sm text-slate-300 flex gap-2">
          <span className="text-cyan-400 mt-0.5">·</span><span>{typeof it === 'string' ? it : JSON.stringify(it)}</span>
        </li>
      ))}
    </ul>
  ) : (
    <p className="text-sm text-slate-300">{empty}</p>
  );

export default function DraftReview({ plan, authHeader, onActivated, onDiscarded }) {
  const [excluded, setExcluded] = useState(new Set());
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);

  const milestones = plan.milestones || [];
  const tasks = plan.tasks || [];
  const academic = plan.academic_plan || {};

  const grouped = useMemo(() => {
    const milestones = plan.milestones || [];
    const tasks = plan.tasks || [];
    const map = new Map();
    milestones.forEach((m) => map.set(m.id, { milestone: m, tasks: [] }));
    tasks.forEach((t) => {
      if (!map.has(t.milestone_id)) map.set(t.milestone_id, { milestone: { id: t.milestone_id, title: t.milestone_title || 'Other' }, tasks: [] });
      map.get(t.milestone_id).tasks.push(t);
    });
    return [...map.values()].filter((g) => g.tasks.length);
  }, [plan]);

  const toggle = (id) => {
    const next = new Set(excluded);
    next.has(id) ? next.delete(id) : next.add(id);
    setExcluded(next);
  };

  const activate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/futurepath-plan/activate`,
        { excluded_task_ids: [...excluded], edits }, authHeader);
      toast.success(`Plan activated, ${r.data.tasks_created} tasks are live on your board.`);
      onActivated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not activate the plan.');
    } finally {
      setBusy(false);
    }
  };

  const discard = async () => {
    if (!window.confirm('Discard this draft and start over?')) return;
    try {
      await axios.delete(`${API}/futurepath-plan/draft`, authHeader);
      onDiscarded();
    } catch {
      toast.error('Could not discard the draft.');
    }
  };

  const approvedCount = tasks.length - excluded.size;

  return (
    <div className="space-y-6" data-testid="draft-review">
      <div className="glass-card p-6">
        <p className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold">Draft · review before you activate</p>
        <h2 className="font-outfit text-2xl font-bold text-white mt-1">{plan.title}</h2>
        <p className="text-slate-300 text-sm mt-2">{plan.goal}</p>
        <div className="flex flex-wrap gap-4 mt-4 text-sm">
          <span className="text-slate-300">Endpoint: <span className="text-white">{plan.horizon?.label}</span></span>
          <span className="text-slate-300">Target: <span className="text-white">{fmtDate(plan.horizon?.target_date)}</span></span>
          <span className="text-slate-300">Span: <span className="text-white">{plan.horizon?.months} months</span></span>
          <span className="text-slate-300">Phases: <span className="text-white">{(plan.phases || []).length}</span></span>
          <span className="text-slate-300">Milestones: <span className="text-white">{milestones.length}</span></span>
          <span className="text-slate-300">Tasks: <span className="text-white">{tasks.length}</span></span>
        </div>
        {plan.endpoint_summary && <p className="text-sm text-slate-300 mt-3">{plan.endpoint_summary}</p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Section title="Pathways" icon={Flag} testid="draft-pathways">
          <p className="text-sm text-white font-medium">{plan.pathway?.primary?.name}</p>
          <p className="text-sm text-slate-300 mt-1">{plan.pathway?.primary?.description}</p>
          <p className="text-sm text-white font-medium mt-4">Backup: {plan.pathway?.backup?.name}</p>
          <p className="text-sm text-slate-300 mt-1">{plan.pathway?.backup?.description}</p>
        </Section>

        <Section title="Financial assumptions" icon={Wallet} testid="draft-financial">
          <p className="text-sm text-slate-300">Estimated cost: <span className="text-white">{plan.financial_assumptions?.estimated_total_cost || '-'}</span></p>
          <p className="text-sm text-slate-300 mt-2">{plan.financial_assumptions?.funding_strategy}</p>
          <div className="mt-3"><List items={plan.financial_assumptions?.aid_steps} empty="No aid steps yet" /></div>
          {plan.financial_assumptions?.risk_notes && (
            <p className="text-xs text-amber-300/80 mt-3">{plan.financial_assumptions.risk_notes}</p>
          )}
        </Section>
      </div>

      <Section title="Academic roadmap" icon={GraduationCap} testid="draft-academic">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          {[['Grade', academic.current_grade], ['Current GPA', academic.current_gpa], ['Target GPA', academic.target_gpa]]
            .map(([k, v]) => (
              <div key={k} className="rounded-lg border border-slate-700/70 bg-slate-900/40 p-3">
                <p className="text-[11px] uppercase tracking-wider text-slate-300">{k}</p>
                <p className="text-sm text-white mt-0.5">{v || '-'}</p>
              </div>
            ))}
        </div>
        {(academic.course_progression || []).length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-300 border-b border-slate-700">
                  <th className="py-2 pr-3">Year</th><th className="py-2 pr-3">Math</th>
                  <th className="py-2 pr-3">Science</th><th className="py-2 pr-3">Other / advanced</th>
                </tr>
              </thead>
              <tbody>
                {academic.course_progression.map((row, i) => (
                  <tr key={i} className="border-b border-slate-800/60 align-top">
                    <td className="py-2 pr-3 text-white whitespace-nowrap">{row.year}</td>
                    <td className="py-2 pr-3 text-slate-300">{row.math}</td>
                    <td className="py-2 pr-3 text-slate-300">{row.science}</td>
                    <td className="py-2 pr-3 text-slate-300">
                      {[...(row.other_courses || []), ...(row.advanced_opportunities || [])].join(', ')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
          <div><p className="text-xs uppercase tracking-wider text-slate-300 mb-2">Testing</p>
            <List items={(academic.standardized_testing || []).map((t) => `${t.test}, ${t.when}${t.target ? ` (target ${t.target})` : ''}`)} /></div>
          <div><p className="text-xs uppercase tracking-wider text-slate-300 mb-2">Graduation requirements</p>
            <List items={academic.graduation_requirements} /></div>
          <div><p className="text-xs uppercase tracking-wider text-slate-300 mb-2">Activities, work & volunteering</p>
            <List items={[...(academic.extracurriculars || []), ...(academic.work_experience || []), ...(academic.volunteer_experience || [])]} /></div>
          <div><p className="text-xs uppercase tracking-wider text-slate-300 mb-2">Application & aid readiness</p>
            <List items={[...(academic.application_readiness || []), ...(academic.financial_aid_readiness || [])]} /></div>
        </div>
      </Section>

      <div className="glass-card p-5" data-testid="draft-backlog">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">Your backlog</h3>
            <p className="text-xs text-slate-300 mt-1">Uncheck anything you don't want. You can adjust dates too.</p>
          </div>
          <span className="text-sm text-white">{approvedCount} of {tasks.length} approved</span>
        </div>

        <div className="space-y-5">
          {grouped.map(({ milestone, tasks: group }) => (
            <div key={milestone.id || milestone.title}>
              <p className="text-sm font-semibold text-white flex items-center gap-2">
                <Flag className="w-3.5 h-3.5 text-cyan-300" /> {milestone.title}
                {milestone.target_date && <span className="text-xs text-slate-300 font-normal">· {fmtDate(milestone.target_date)}</span>}
              </p>
              <div className="mt-2 space-y-2">
                {group.map((t) => {
                  const cat = CATEGORY_META[t.category] || CATEGORY_META.personal;
                  const off = excluded.has(t.id);
                  return (
                    <div key={t.id}
                      className={`rounded-xl border p-3 transition-colors duration-200 ${off ? 'border-slate-800 bg-slate-900/20 opacity-50' : 'border-slate-700/80 bg-slate-900/40'}`}
                      data-testid={`draft-task-${t.id}`}>
                      <div className="flex items-start gap-3">
                        <button onClick={() => toggle(t.id)} data-testid={`draft-task-toggle-${t.id}`} className="mt-0.5">
                          {off ? <Circle className="w-4 h-4 text-slate-300" /> : <CheckCircle2 className="w-4 h-4 text-cyan-300" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white font-medium">{t.title}</p>
                          <p className="text-xs text-slate-300 mt-0.5">{t.description}</p>
                          {t.why && <p className="text-xs text-cyan-300/70 mt-1">Why: {t.why}</p>}
                          <div className="flex flex-wrap items-center gap-2 mt-2">
                            <span className={`text-[11px] px-2 py-0.5 rounded-full ${cat.bg} ${cat.text}`}>{cat.label}</span>
                            <span className={`text-[11px] px-2 py-0.5 rounded-full ${PRIORITY_CLASS[t.priority] || ''}`}>{t.priority}</span>
                            {t.estimated_effort && <span className="text-[11px] text-slate-300">{t.estimated_effort}</span>}
                            {(t.depends_on || []).length > 0 && <span className="text-[11px] text-amber-300/80">{t.depends_on.length} dependency</span>}
                          </div>
                        </div>
                        <input type="date" value={(edits[t.id]?.due_date || t.due_date || '').slice(0, 10)}
                          onChange={(e) => setEdits({ ...edits, [t.id]: { ...(edits[t.id] || {}), due_date: e.target.value } })}
                          className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-white"
                          data-testid={`draft-task-date-${t.id}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button onClick={activate} disabled={busy || approvedCount === 0}
          className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold disabled:opacity-50"
          data-testid="activate-plan-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
          Activate my plan ({approvedCount} tasks)
        </button>
        <button onClick={discard} className="text-sm text-slate-300 hover:text-rose-300 inline-flex items-center gap-1.5"
          data-testid="discard-draft-btn">
          <Trash2 className="w-4 h-4" /> Discard and start over
        </button>
      </div>
    </div>
  );
}
